import { Badge, BlockStack, Button, Icon, InlineStack, ProgressBar, Text } from '@shopify/polaris';
import { ArchiveIcon, ArrowDownIcon, FileIcon, UndoIcon, ViewIcon } from '@shopify/polaris-icons';
import React from 'react';
import { FitmentImportHistoryItem } from '../../app/api/agent';

export const formatLabelMap: Record<string, string> = {
    csv_xlsx: 'CSV/XLSX',
    collection_mappings_csv_xlsx: 'Collection mappings (CSV/XLSX)',
    aces_xml: 'ACES XML',
    atd_price_file: 'ATD Price File',
    product_tags: 'Product Tags',
    fitment_export: 'Fitment Export',
    unknown: 'Unknown',
};

const formatStageLabel = (stage?: string) => {
    if (!stage) {
        return null;
    }

    switch (stage.trim().toLowerCase()) {
        case 'processing':
            return 'Processing';
        case 'resolving_skus':
            return 'Resolving SKUs';
        case 'waiting_shopify_sync':
            return 'Waiting for Shopify sync';
        case 'processing_recovered_skus':
            return 'Processing recovered SKUs';
        case 'parsing':
            return 'Parsing';
        case 'applying':
            return 'Applying';
        case 'pricing':
            return 'Updating prices';
        case 'reading':
            return 'Reading tags';
        case 'reading_collection_mappings':
            return 'Reading collection mappings';
        case 'resolving_collections':
            return 'Resolving collections';
        case 'creating_vehicles':
            return 'Creating vehicle records';
        case 'exporting':
            return 'Exporting';
        case 'tagging':
            return 'Tagging';
        case 'completed':
            return 'Completed';
        case 'completed_with_skipped_skus':
            return 'Completed with skipped SKUs';
        case 'completed_with_review':
            return 'Completed with review';
        case 'failed':
            return 'Failed';
        default:
            return stage;
    }
};

export const getStatusBadge = (state: string) => {
    const normalizedState = state.trim().toLowerCase();

    if (normalizedState === 'completed' || normalizedState === 'complete') {
        return <Badge tone="success">Complete</Badge>;
    }

    if (normalizedState === 'completed with skipped skus') {
        return <Badge tone="warning">Completed with skipped SKUs</Badge>;
    }

    if (normalizedState === 'completed with review') {
        return <Badge tone="warning">Completed with review</Badge>;
    }

    if (normalizedState === 'pending review') {
        return <Badge tone="warning">Pending review</Badge>;
    }

    if (normalizedState === 'applied') {
        return <Badge tone="success">Applied</Badge>;
    }

    if (normalizedState === 'undone') {
        return <Badge>Undone</Badge>;
    }

    if (normalizedState === 'failed' || normalizedState === 'error') {
        return <Badge tone="critical">Error</Badge>;
    }

    if (normalizedState === 'queued' || normalizedState === 'pending') {
        return <Badge tone="info">Queued</Badge>;
    }

    if (normalizedState === 'waiting for shopify sync') {
        return <Badge progress="partiallyComplete" tone="info">Waiting for Shopify sync</Badge>;
    }

    if (normalizedState === 'resolving skus') {
        return <Badge progress="partiallyComplete" tone="info">Resolving SKUs</Badge>;
    }

    if (normalizedState === 'processing recovered skus') {
        return <Badge progress="partiallyComplete" tone="info">Processing recovered SKUs</Badge>;
    }

    if (normalizedState === 'processing' || normalizedState === 'in_progress' || normalizedState === 'in progress') {
        return <Badge progress="partiallyComplete" tone="info">In progress</Badge>;
    }

    if (normalizedState === 'exporting') {
        return <Badge progress="partiallyComplete" tone="info">Exporting</Badge>;
    }

    if (normalizedState === 'tagging in progress') {
        return <Badge progress="partiallyComplete" tone="info">Tagging in progress</Badge>;
    }

    if (normalizedState === 'tagging complete') {
        return <Badge tone="success">Tagging complete</Badge>;
    }

    if (normalizedState === 'tagging failed') {
        return <Badge tone="critical">Tagging failed</Badge>;
    }

    if (normalizedState === 'cancelled' || normalizedState === 'canceled') {
        return <Badge tone="warning">Cancelled</Badge>;
    }

    return <Badge>{state}</Badge>;
};

export const getMappingRulesSummary = (item: FitmentImportHistoryItem) => {
    if (item.importFormat === 'atd_price_file') {
        return 'Updates variant prices from MAP and inventory item costs';
    }

    if (item.importFormat === 'product_tags') {
        return 'Stages Shopify product tags for review before changing fitment mappings';
    }

    if (item.importFormat === 'fitment_export') {
        return 'Exports active product and variant fitment assignments';
    }

    if (item.importFormat === 'csv_xlsx') {
        return item.csvXlsxImportMode === 'replace_matched_skus'
            ? 'Replaced existing fitments for matched SKUs'
            : 'Appended fitments to existing mappings';
    }

    if (item.importFormat === 'collection_mappings_csv_xlsx') {
        return 'Upserted vehicle-to-collection mappings; product mappings were unchanged';
    }

    if (!item.restrictToVendor) {
        return 'Store-wide vendor mapping';
    }

    const vendorLabel = item.selectedVendorName || 'Unknown vendor';
    const normalizeLabel = item.normalizeSku ? 'SKU normalization on' : 'SKU normalization off';

    if (!item.useVendorSkuSuffixRule) {
        return `${vendorLabel} · SKU suffix rule off · ${normalizeLabel}`;
    }

    const suffixRule = item.vendorSkuSuffixRule?.trim() || '(empty)';
    const updateLabel = item.updateSavedVendorSkuSuffixRule ? 'saved as default' : 'import only';

    return `${vendorLabel} · Suffix "${suffixRule}" (${updateLabel}) · ${normalizeLabel}`;
};

const formatCount = (value?: number) => (typeof value === 'number' ? value.toLocaleString() : '0');

type ImportMetric = {
    label: string;
    value: string;
};

const hasRecordMetrics = (item: FitmentImportHistoryItem) => Boolean(
    item.parsedRecordCount
    || item.appliedRecordCount
    || item.declaredRecordCount
    || item.csvXlsxImportedVehicleRecordCount
    || item.csvXlsxAppliedAssignmentCount
    || item.collectionMappingCreatedVehicleRecordCount
    || item.collectionMappingAddedCount
    || item.collectionMappingReassignedCount
    || item.collectionMappingUnchangedCount
    || item.collectionMappingSkippedRowCount
);

const getRecordMetrics = (item: FitmentImportHistoryItem): ImportMetric[] => {
    if (item.importFormat === 'fitment_export') {
        return [{
            label: 'Assignments exported',
            value: formatCount(item.appliedRecordCount ?? item.parsedRecordCount),
        }];
    }

    if (item.importFormat === 'csv_xlsx') {
        const metrics: ImportMetric[] = [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
        ];

        if (typeof item.csvXlsxImportedVehicleRecordCount === 'number') {
            metrics.push({
                label: 'Vehicle records',
                value: formatCount(item.csvXlsxImportedVehicleRecordCount),
            });
        }

        metrics.push({
            label: 'Product assignments',
            value: formatCount(item.csvXlsxAppliedAssignmentCount ?? item.appliedRecordCount),
        });

        return metrics;
    }

    if (item.importFormat === 'collection_mappings_csv_xlsx') {
        return [
            { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
            { label: 'Vehicles created', value: formatCount(item.collectionMappingCreatedVehicleRecordCount) },
            { label: 'Mappings added', value: formatCount(item.collectionMappingAddedCount) },
            { label: 'Reassigned', value: formatCount(item.collectionMappingReassignedCount) },
            { label: 'Already mapped', value: formatCount(item.collectionMappingUnchangedCount) },
            { label: 'Review rows', value: formatCount(item.collectionMappingSkippedRowCount) },
        ];
    }

    return [
        { label: 'Rows declared', value: formatCount(item.declaredRecordCount) },
        { label: 'Rows parsed', value: formatCount(item.parsedRecordCount) },
        { label: 'Rows applied', value: formatCount(item.appliedRecordCount) },
    ];
};

const formatImportDate = (value: string) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
};

type ImportDateGroup = {
    key: string;
    label: string;
};

const getLocalDateKey = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
].join('-');

const getImportDateGroup = (value: string): ImportDateGroup => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return {
            key: `unknown-${value}`,
            label: 'Date unavailable',
        };
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const key = getLocalDateKey(date);

    if (key === getLocalDateKey(today)) {
        return { key, label: 'Today' };
    }

    if (key === getLocalDateKey(yesterday)) {
        return { key, label: 'Yesterday' };
    }

    return {
        key,
        label: new Intl.DateTimeFormat(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }).format(date),
    };
};

const getHistoryItemTone = (state: string) => {
    const normalizedState = state.trim().toLowerCase();

    if (normalizedState.includes('failed') || normalizedState.includes('error')) {
        return 'critical';
    }

    if (normalizedState.includes('skipped') || normalizedState.includes('pending review') || normalizedState.includes('with review')) {
        return 'warning';
    }

    if (
        normalizedState.includes('processing')
        || normalizedState.includes('queued')
        || normalizedState.includes('waiting')
        || normalizedState.includes('resolving')
        || normalizedState.includes('exporting')
        || normalizedState.includes('tagging in progress')
    ) {
        return 'info';
    }

    if (
        normalizedState.includes('complete')
        || normalizedState === 'applied'
        || normalizedState === 'tagging complete'
    ) {
        return 'success';
    }

    return 'default';
};

type StageProgress = {
    label: string;
    value: number;
};

const getStageProgress = (item: FitmentImportHistoryItem): StageProgress | null => {
    const stage = item.processingStage?.trim().toLowerCase();
    const declared = item.declaredRecordCount;
    const parsed = item.parsedRecordCount;
    const applied = item.appliedRecordCount;

    if (stage === 'parsing' && typeof declared === 'number' && declared > 0 && typeof parsed === 'number') {
        const value = Math.round((Math.min(parsed, declared) / declared) * 100);
        return {
            label: `Parsing rows ${formatCount(parsed)} / ${formatCount(declared)}`,
            value,
        };
    }

    if (stage === 'applying' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Applying rows ${formatCount(applied)} / ${formatCount(parsed)}`,
            value,
        };
    }

    if (stage === 'pricing' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Updating matched variants ${formatCount(applied)} / ${formatCount(parsed)}`,
            value,
        };
    }

    if (stage === 'exporting') {
        return {
            label: 'Writing fitment export CSV',
            value: typeof item.progressPercent === 'number'
                ? Math.max(0, Math.min(100, item.progressPercent))
                : 0,
        };
    }

    if (stage === 'tagging') {
        if (typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0) {
            const processed = Math.max(0, Math.min(item.taggingProcessedCount ?? 0, item.taggingTotalCount));
            const value = Math.round((processed / item.taggingTotalCount) * 100);
            const actionLabel = item.taggingPhase?.trim().toLowerCase() === 'fetching'
                ? 'Fetching product tags'
                : 'Applying product tags';

            return {
                label: `${actionLabel} ${formatCount(processed)} / ${formatCount(item.taggingTotalCount)}`,
                value,
            };
        }

        return {
            label: 'Tagging changed products',
            value: typeof item.progressPercent === 'number'
                ? Math.max(0, Math.min(100, item.progressPercent))
                : 0,
        };
    }

    return null;
};

const getStageDiagnostics = (item: FitmentImportHistoryItem) => {
    switch (item.processingStage?.trim().toLowerCase()) {
        case 'parsing':
            return 'Parsing the input file and normalizing source rows.';
        case 'applying':
            return 'Writing parsed rows to fitment records and deduplicating.';
        case 'resolving_skus':
            return 'Looking up missing SKUs directly in Shopify.';
        case 'waiting_shopify_sync':
            return 'Waiting for Shopify product and variant sync to finish.';
        case 'processing_recovered_skus':
            return 'Applying fitment rows for SKUs recovered from Shopify.';
        case 'pricing':
            return 'Matching ATD part numbers to Shopify variant SKUs and updating prices.';
        case 'exporting':
            return 'Writing assigned product fitments to a downloadable CSV.';
        case 'reading':
            return 'Reading synced Shopify product tags.';
        case 'reading_collection_mappings':
            return 'Reading and normalizing the collection mapping spreadsheet.';
        case 'resolving_collections':
            return 'Matching spreadsheet destinations to the shop\'s synced Shopify collections.';
        case 'creating_vehicles':
            return 'Creating any canonical vehicle records that do not exist yet.';
        case 'tagging':
            return 'Refreshing product tags for changed fitments.';
        default:
            return null;
    }
};

type Props = {
    items: FitmentImportHistoryItem[];
    archivingIds: Record<number, boolean>;
    onArchive: (queueId: number) => void;
    onDownload: (item: FitmentImportHistoryItem) => void;
    onDownloadReview?: (item: FitmentImportHistoryItem) => void;
    downloadingIds: Record<number, boolean>;
    downloadingReviewIds?: Record<number, boolean>;
    reviewingProductTagIds?: Record<number, boolean>;
    undoingProductTagIds?: Record<number, boolean>;
    onReviewProductTags?: (item: FitmentImportHistoryItem) => void;
    onUndoProductTags?: (item: FitmentImportHistoryItem) => void;
};

type GroupedHistoryItems = ImportDateGroup & {
    items: FitmentImportHistoryItem[];
};

const groupHistoryItems = (items: FitmentImportHistoryItem[]) => (
    items.reduce<GroupedHistoryItems[]>((groups, item) => {
        const dateGroup = getImportDateGroup(item.addedOn);
        const currentGroup = groups[groups.length - 1];

        if (currentGroup?.key === dateGroup.key) {
            currentGroup.items.push(item);
            return groups;
        }

        groups.push({
            ...dateGroup,
            items: [item],
        });
        return groups;
    }, [])
);

const getHistoryTimingLabel = (item: FitmentImportHistoryItem, stageLabel: string | null) => {
    const normalizedState = item.state.trim().toLowerCase();
    const isTerminalState = normalizedState.includes('complete')
        || normalizedState.includes('failed')
        || normalizedState.includes('error')
        || normalizedState === 'applied'
        || normalizedState === 'undone'
        || normalizedState === 'cancelled'
        || normalizedState === 'canceled';

    if (item.completedAt) {
        return `Finished ${formatImportDate(item.completedAt)}`;
    }

    if (item.startedAt) {
        return stageLabel && !isTerminalState
            ? `${stageLabel} · Started ${formatImportDate(item.startedAt)}`
            : `Started ${formatImportDate(item.startedAt)}`;
    }

    return isTerminalState ? null : stageLabel;
};

const ImportHistoryCards = ({
    items,
    archivingIds,
    onArchive,
    onDownload,
    onDownloadReview,
    downloadingIds,
    downloadingReviewIds = {},
    reviewingProductTagIds = {},
    undoingProductTagIds = {},
    onReviewProductTags,
    onUndoProductTags,
}: Props) => {
    const groupedItems = groupHistoryItems(items);

    return (
        <div className="pf-import-history-list">
            {groupedItems.map((group, groupIndex) => {
                const headingId = `pf-import-history-date-${group.key}-${groupIndex}`;

                return (
                    <section
                        aria-labelledby={headingId}
                        className="pf-import-history-group"
                        key={`${group.key}-${groupIndex}`}
                    >
                        <div className="pf-import-history-group__header">
                            <Text as="h3" variant="headingSm" id={headingId}>
                                {group.label}
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">
                                {group.items.length} {group.items.length === 1 ? 'run' : 'runs'}
                            </Text>
                        </div>

                        <div className="pf-import-history-group__items" role="list">
                            {group.items.map((item) => {
            const progressValue = typeof item.progressPercent === 'number'
                ? Math.max(0, Math.min(100, item.progressPercent))
                : undefined;
            const stageLabel = formatStageLabel(item.processingStage);
            const timingLabel = getHistoryTimingLabel(item, stageLabel);
            const normalizedState = item.state.toLowerCase();
            const isProcessingState = normalizedState === 'processing'
                || normalizedState.includes('tagging')
                || normalizedState.includes('waiting for shopify sync')
                || normalizedState.includes('resolving skus')
                || normalizedState.includes('processing recovered skus')
                || normalizedState.includes('exporting');
            const showProgress = isProcessingState && (progressValue === undefined || progressValue < 100);
            const stageProgress = getStageProgress(item);
            const stageDiagnostics = getStageDiagnostics(item);
            const recordMetrics = getRecordMetrics(item);

            return (
                <article
                    className={`pf-import-history-item pf-import-history-item--${getHistoryItemTone(item.state)}`}
                    key={item.queueId}
                    role="listitem"
                >
                        <header className="pf-import-history-item__header">
                            <div className="pf-import-history-item__identity">
                                <span className="pf-import-history-item__file-icon" aria-hidden="true">
                                    <Icon source={FileIcon} tone="subdued" />
                                </span>
                                <div className="pf-import-history-item__title">
                                    <Text as="h4" variant="headingSm">
                                        {item.originalFileName || 'Unnamed file'}
                                    </Text>
                                    <InlineStack gap="200" blockAlign="center" wrap>
                                        <Text as="span" variant="bodySm" tone="subdued">
                                            {formatLabelMap[item.importFormat] ?? item.importFormat}
                                        </Text>
                                        <span className="pf-import-history-item__meta-separator" aria-hidden="true">•</span>
                                        <Text as="span" variant="bodySm" tone="subdued">
                                            Queued {formatImportDate(item.addedOn)}
                                        </Text>
                                    </InlineStack>
                                </div>
                            </div>

                            <InlineStack gap="200" blockAlign="center" wrap>
                                {getStatusBadge(item.state)}
                                {item.isArchived && <Badge>Archived</Badge>}
                            </InlineStack>
                        </header>

                        <div className="pf-import-history-item__body">
                            <BlockStack gap="300">
                                <Text as="p" variant="bodySm">
                                    {getMappingRulesSummary(item)}
                                </Text>

                                {hasRecordMetrics(item) && (
                                    <div className={`pf-import-history-item__metrics${recordMetrics.length === 1 ? ' pf-import-history-item__metrics--single' : ''}`}>
                                        {recordMetrics.map((metric) => (
                                            <div className="pf-import-history-item__metric" key={metric.label}>
                                                <Text as="span" variant="bodySm" tone="subdued">
                                                    {metric.label}
                                                </Text>
                                                <Text as="span" variant="headingSm">
                                                    {metric.value}
                                                </Text>
                                            </div>
                                        ))}
                                    </div>
                                )}

                        {showProgress && (
                            <div className="pf-import-history-item__progress">
                                <BlockStack gap="100">
                                    <InlineStack align="space-between">
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {item.processingMessage || stageLabel || 'Import in progress'}
                                        </Text>
                                        {progressValue !== undefined && (
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {progressValue}%
                                            </Text>
                                        )}
                                    </InlineStack>
                                    {progressValue !== undefined && <ProgressBar progress={progressValue} size="small" />}

                                    {stageProgress && (
                                        <BlockStack gap="050">
                                            <InlineStack align="space-between">
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {stageProgress.label}
                                                </Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {stageProgress.value}%
                                                </Text>
                                            </InlineStack>
                                            <ProgressBar progress={stageProgress.value} size="small" />
                                        </BlockStack>
                                    )}
                                </BlockStack>
                            </div>
                        )}

                        {stageDiagnostics && (
                            <Text as="p" variant="bodySm" tone="subdued">
                                {stageDiagnostics}
                            </Text>
                        )}

                        {typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0 && (
                            <Text as="p" variant="bodySm" tone="subdued">
                                Tagging: Processed {formatCount(item.taggingProcessedCount)} / {formatCount(item.taggingTotalCount)}, Updated {formatCount(item.taggingUpdatedCount)}
                            </Text>
                        )}

                        {item.importFormat === 'product_tags' && typeof item.productTagPreviewTotalCount === 'number' && (
                            <Text as="p" variant="bodySm" tone="subdued">
                                Preview: {formatCount(item.productTagWillApplyCount)} row(s) ready, {formatCount(item.productTagWillCreateCount)} new vehicle value(s), {formatCount(item.productTagSkippedCount)} skipped across {formatCount(item.productTagPreviewProductCount)} product(s).
                            </Text>
                        )}

                                {item.error && (
                                    <div className="pf-import-history-item__error">
                                        <Text as="p" variant="bodySm" tone="critical">
                                            {item.error}
                                        </Text>
                                    </div>
                                )}
                            </BlockStack>
                        </div>

                        <footer className="pf-import-history-item__footer">
                            <InlineStack gap="200" blockAlign="center" wrap>
                                {timingLabel && (
                                    <Text as="span" variant="bodySm" tone="subdued">
                                        {timingLabel}
                                    </Text>
                                )}
                                {item.isArchived && item.archivedAt && (
                                    <>
                                        {timingLabel && <span className="pf-import-history-item__meta-separator" aria-hidden="true">•</span>}
                                        <Text as="span" variant="bodySm" tone="subdued">
                                            Archived {formatImportDate(item.archivedAt)}
                                        </Text>
                                    </>
                                )}
                            </InlineStack>

                            <InlineStack gap="100" blockAlign="center" wrap>
                                {item.importFormat === 'product_tags' && onReviewProductTags && (
                                    <Button
                                        icon={ViewIcon}
                                        size="slim"
                                        onClick={() => onReviewProductTags(item)}
                                        loading={!!reviewingProductTagIds[item.queueId]}
                                    >
                                        {item.awaitingProductTagApproval ? 'Review' : 'Preview'}
                                    </Button>
                                )}
                                {item.importFormat === 'product_tags' && item.productTagReviewStatus?.toLowerCase() === 'applied' && onUndoProductTags && (
                                    <Button
                                        icon={UndoIcon}
                                        size="slim"
                                        tone="critical"
                                        variant="tertiary"
                                        onClick={() => onUndoProductTags(item)}
                                        loading={!!undoingProductTagIds[item.queueId]}
                                    >
                                        Undo
                                    </Button>
                                )}
                                {(item.importFormat === 'csv_xlsx' || item.importFormat === 'collection_mappings_csv_xlsx') && item.storedFileName && onDownloadReview && (
                                    <Button
                                        icon={ViewIcon}
                                        size="slim"
                                        onClick={() => onDownloadReview(item)}
                                        loading={!!downloadingReviewIds[item.queueId]}
                                    >
                                        Review file
                                    </Button>
                                )}
                                {item.storedFileName && (
                                    <Button
                                        icon={ArrowDownIcon}
                                        size="slim"
                                        variant="tertiary"
                                        onClick={() => onDownload(item)}
                                        loading={!!downloadingIds[item.queueId]}
                                    >
                                        Download
                                    </Button>
                                )}
                                {!item.isArchived && (
                                    <Button
                                        accessibilityLabel={`Archive ${item.originalFileName || 'import'}`}
                                        icon={ArchiveIcon}
                                        size="slim"
                                        tone="critical"
                                        variant="tertiary"
                                        onClick={() => onArchive(item.queueId)}
                                        loading={!!archivingIds[item.queueId]}
                                    >
                                        Archive
                                    </Button>
                                )}
                            </InlineStack>
                        </footer>
                </article>
                                );
                            })}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default ImportHistoryCards;
